import { Actor, AnyEventObject, createActor, createMachine, InspectionEvent } from 'xstate'

import { getFlowName, getProductName } from '@dg-util'
import BrandFSMInput from 'BrandFSMInput'
import { get } from 'lodash'
import {
  FlowMachineType,
  FlowServiceType,
  FSMStateType,
  StateRoute,
} from '../types/interface/fsm.interface'
import {
  getPersistedLastEventType,
  getPersistedState,
  persistState,
  setClientHistory,
  shouldSkipHistorySync,
} from './util'
import appHistory from '../routes/history'
import { forwardInspection } from './inspector'

export class FSMService {
  private actorStarted = false
  private actor!: Actor<FlowMachineType>

  // Stable façade over the live actor. On v4 the interpreter object survived a `restart()`, so
  // anything holding `flowService` stayed valid; on v5 rehydration means a *new* actor, which
  // would silently strand every consumer that captured the reference (AppWrapper's `AgentStatus`
  // prop, and any test spying on `send`). The façade keeps one identity for the app's lifetime
  // and forwards to whichever actor is current.
  flowService: FlowServiceType = {
    send: (event: AnyEventObject) => this.actor.send(event),
    subscribe: (...args: Parameters<Actor<FlowMachineType>['subscribe']>) =>
      this.actor.subscribe(...args),
    getSnapshot: () => this.actor.getSnapshot(),
    start: () => this.actor.start(),
    stop: () => this.actor.stop(),
  } as unknown as FlowServiceType
  flowMachine!: FlowMachineType
  routes!: StateRoute[]
  currentState!: FSMStateType
  // v5 snapshots carry no `.event`. Pages that need the event type that produced the current
  // state (Auth error handling, Find's WRONG_PRODUCT_ID message) read this instead. Persisted
  // with the FSM state so it survives a reload, as `state.event` did.
  lastEventType?: string
  onTransitionCallback(newState: FSMStateType): void {
    this.currentState = newState
  }
  initMachineCallbacks: ((fsmService: FSMService) => void)[] = []
  setInitMachineCallback(callBack: (fsmService: FSMService) => void): void {
    this.initMachineCallbacks.push(callBack)
  }

  constructor() {
    this.initMachine()
  }
  initMachine() {
    const flowName = getFlowName()
    const productName = getProductName()
    const input = get(BrandFSMInput, [flowName, productName])

    const flowMachine = createMachine(input.config, input.options)

    this.flowMachine = flowMachine
    this.routes = input.routes
    this.createActor()
    this.initMachineCallbacks.forEach((initMachineCallback) => {
      initMachineCallback(this)
    })
  }

  // v5 takes the restored snapshot at actor-creation time, not as a `start()` argument, so a
  // rehydrate means a new actor. Consumers holding the old one are refreshed through
  // `initMachineCallbacks`.
  private createActor() {
    // Rehydrate the machine from the last valid state for the current product, or from
    // a one-shot preserved state when another flow/product redirected into this one.
    const persistedState = getPersistedState(this.flowMachine)

    this.lastEventType = getPersistedLastEventType()

    this.actor = createActor(this.flowMachine, {
      snapshot: persistedState,
      inspect: (event: InspectionEvent) => {
        // `@xstate.event` fires for every event sent to the machine, including ones that cause
        // no transition — which is what v4's `state.event` did as well.
        //
        // `xstate.init` is excluded deliberately: it fires on every `start()`, including a
        // rehydrated one, and would overwrite the event type restored from storage. v4 had no
        // equivalent, because `start(persistedState)` carried the serialized `_event` through.
        if (
          event.type === '@xstate.event' &&
          event.actorRef === this.actor &&
          event.event.type !== 'xstate.init'
        ) {
          this.lastEventType = event.event.type
        }

        forwardInspection(event)
      },
    })

    this.actorStarted = false

    let isInitialEmission = true

    this.actor.subscribe((snapshot) => {
      const currentState = snapshot as FSMStateType

      // Keep the browser URL aligned with FSM transitions, except on the initial boot of
      // external callback pages where the raw callback path must survive for page handlers.
      // v4 identified that first emission by `state.changed === undefined`; v5 snapshots have
      // no `changed`, so the first emission is tracked here instead.
      if (
        !shouldSkipHistorySync(appHistory.location.pathname, isInitialEmission ? undefined : true)
      ) {
        setClientHistory(currentState.value)
      }

      isInitialEmission = false

      // Persist every resolved state so reloads can resume the same flow/product journey.
      persistState(currentState, this.lastEventType)

      this.onTransitionCallback(currentState)
      this.currentState = currentState
    })
  }

  restart() {
    // A v5 actor cannot be restarted once stopped, so a second `restart()` needs a fresh one.
    if (this.actorStarted) {
      this.actor.stop()
      this.createActor()
    }

    this.actorStarted = true
    this.actor.start()
  }
}

export default new FSMService()
